import React, {Component, createRef, ReactElement, RefObject, useRef, useState} from 'react';
import {Button, Grid, IconButton, Typography} from "@mui/material";
import {StyledBoxRow} from "./StyledComponents";
import {colorBlue, colorGreen, nightGray, translucentBlack} from "./colors";
import {
  CameraAltOutlined,
  CameraswitchOutlined,
  CheckOutlined,
  EditOutlined,
  ReplayOutlined
} from "@mui/icons-material";
import {PD_XLG, SZ_SM, SZ_SMSM, SZ_XLG} from "./dimens";
import {FileUtil} from "./file_util";
import {BaseApp} from "./BaseApp";

export enum ImageEditorImageType {
  IMG_DATA = "imgData",
  BASE64URL = "base64url",
}

export type ImageEditorImage = {
  data: any,
  type: ImageEditorImageType,
  cropped?: boolean,
}

export type ImageOptions = {
  keepAspectRatio?: number;
}

export interface ImageEditor {

  render(image: ImageEditorImage, onDone: (image: ImageEditorImage) => void, options?: ImageOptions): ReactElement;
}

export type FacingMode = 'user' | 'environment';
export type AspectRatio = 'cover' | number; // for example 16/9, 4/3, 1/1
export type Stream = MediaStream | null;
export type SetStream = (stream: Stream) => void;
export type SetNumCameras = (numCameras: number) => void;
export type SetNotSupported = (notSupported: boolean) => void;
export type SetPermissionDenied = (permissionDenied: boolean) => void;

export type CameraRefs = {
  player: RefObject<HTMLVideoElement>;
  overlay: RefObject<HTMLCanvasElement>;
  canvas: RefObject<HTMLCanvasElement>;
  container: RefObject<HTMLDivElement>;
}

export type CameraProps = {
  facingMode?: FacingMode;
  aspectRatio?: AspectRatio;
  numCamerasCallback?(numCameras: number): void;
  videoSourceDeviceId?: string | undefined;
  errorMessages?: {
    noCameraAccessible?: string;
    permissionDenied?: string;
    switchCamera?: string;
    canvas?: string;
  };
  videoReadyCallback?(refs: CameraRefs): void;
  videoDestroyCallback?(): void;
}

type CameraState = {
  numCameras: number,
  stream?: Stream,
  facingMode: FacingMode,
  notSupported?: boolean,
  permissionDenied?: boolean,
  torchSupported?: boolean,
  torchOn?: boolean,
}

const DEFAULT_FACING_MODE = 'environment';

class StyledCameraContent extends Component<CameraProps, CameraState> {

  private readonly cameraRefs: CameraRefs = {
    player: createRef<HTMLVideoElement>(),
    overlay: createRef<HTMLCanvasElement>(),
    canvas: createRef<HTMLCanvasElement>(),
    container: createRef<HTMLDivElement>(),
  };
  private context2D: CanvasRenderingContext2D;

  constructor(props: CameraProps, context: any) {
    super({
      facingMode: DEFAULT_FACING_MODE,
      aspectRatio: 'cover',
      errorMessages: {
        noCameraAccessible: 'No camera device accessible. Please connect your camera or try a different browser.',
        permissionDenied: 'Permission denied. Please refresh and give camera permission.',
        switchCamera:
          'It is not possible to switch camera to different one because there is only one video device accessible.',
        canvas: 'Canvas is not supported.',
      },
      ...props,
    }, context);
    this.state = {
      numCameras: 0,
      facingMode: props.facingMode || DEFAULT_FACING_MODE,
    }
  }

  private mounted: boolean = false;

  componentDidMount() {
    this.mounted = true;
    this.doInitCameraStream(this.state.stream);
  }

  componentWillUnmount() {
    this.mounted = false;
    this.doStopCameraStream(this.state.stream);
    this.props.videoDestroyCallback?.();
  }

  componentDidUpdate(prevProps: Readonly<CameraProps>, prevState: Readonly<CameraState>, snapshot?: any) {
    if (prevState.numCameras !== this.state.numCameras) {
      this.props.numCamerasCallback?.(this.state.numCameras);
    }
    if (prevState.torchOn !== this.state.torchOn) {
      this.switchTorch(this.state.torchOn);
    }
    const stream = this.state.stream;
    if (prevState.facingMode !== this.state.facingMode || prevProps.videoSourceDeviceId !== this.props.videoSourceDeviceId) {
      this.doInitCameraStream(stream);
    }
    if (prevState.stream !== stream) {
      this.switchTorch(false)
        .then((success) => this.setState({torchSupported: success}));
      const player = this.cameraRefs.player.current;
      if (stream && player) {
        player.srcObject = stream;
      }
    }
  }

  private doInitCameraStream(stream: Stream): void {
    initCameraStream(
      stream,
      (stream: Stream) => this.setState({stream}),
      this.state.facingMode,
      this.props.videoSourceDeviceId,
      (numCameras: number) => this.setState({numCameras}),
      (notSupported: boolean) => this.setState({notSupported}),
      (permissionDenied: boolean) => this.setState({permissionDenied}),
      Boolean(this.mounted),
    );
  }

  private doStopCameraStream(stream: Stream): void {
    if (stream) {
      stream.getTracks().forEach((track) => {
        track.stop();
      });
    }
  }

  isTorchSupported(): boolean {
    return Boolean(this.state.torchSupported);
  }

  private async switchTorch(on = false): Promise<boolean> {
    if (this.state.stream && navigator?.mediaDevices && this.mounted) {
      const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
      const [track] = this.state.stream.getTracks();
      if (supportedConstraints && 'torch' in supportedConstraints && track) {
        try {
          await track.applyConstraints({advanced: [{torch: on}]} as MediaTrackConstraintSet);
          return true;
        } catch {
          return false;
        }
      }
    }

    return false;
  }

  cropImage(keepAspectRatio?: number): ImageEditorImage {
    const canvas = this.cameraRefs.canvas.current;
    if (!keepAspectRatio) {
      return {
        data: canvas.toDataURL("image/jpeg"),
        type: ImageEditorImageType.BASE64URL,
      }
    } else {
      let cropSize: CropSize;
      const canvasAspectRatio = canvas.width / canvas.height;
      if (canvasAspectRatio < keepAspectRatio) {
        cropSize = {
          width: canvas.width,
          height: canvas.width / keepAspectRatio,
        };
      } else {
        cropSize = {
          width: canvas.height * keepAspectRatio,
          height: canvas.height,
        };
      }
      const cropped = Object.assign(document.createElement('canvas'), {width: cropSize.width, height: cropSize.height});
      const ctx = cropped.getContext('2d');
      ctx.drawImage(canvas, (canvas.width - cropped.width) / 2, (canvas.height - cropped.height) / 2, cropped.width, cropped.height, 0, 0, cropped.width, cropped.height);
      return {
        data: cropped.toDataURL("image/jpeg"),
        type: ImageEditorImageType.BASE64URL,
        cropped: true,
      };
    }
  }

  takePhoto(type: ImageEditorImageType = ImageEditorImageType.BASE64URL): ImageEditorImage {
    if (this.state.numCameras < 1) {
      throw new Error(this.props.errorMessages.noCameraAccessible);
    }

    const canvas = this.cameraRefs.canvas.current;
    if (canvas) {
      const player = this.cameraRefs.player.current;
      const playerWidth = player?.videoWidth || 1280;
      const playerHeight = player?.videoHeight || 720;
      const playerAR = playerWidth / playerHeight;

      const canvasWidth = this.cameraRefs.container.current?.offsetWidth || 1280;
      const canvasHeight = this.cameraRefs.container.current?.offsetHeight || 1280;
      const canvasAR = canvasWidth / canvasHeight;

      let sX, sY, sW, sH, imgData;
      if (playerAR > canvasAR) {
        sH = playerHeight;
        sW = playerHeight * canvasAR;
        sX = (playerWidth - sW) / 2;
        sY = 0;
      } else {
        sW = playerWidth;
        sH = playerWidth / canvasAR;
        sX = 0;
        sY = (playerHeight - sH) / 2;
      }

      canvas.width = sW;
      canvas.height = sH;

      if (!this.context2D) {
        this.context2D = canvas.getContext('2d', {willReadFrequently: true});
      }

      if (this.context2D && player) {
        const mirrored = this.state.facingMode === 'user';
        this.context2D.save();
        if (mirrored) {
          this.context2D.translate(sW, 0);
          this.context2D.scale(-1, 1);
        }
        this.context2D.drawImage(player, sX, sY, sW, sH, 0, 0, sW, sH);
        this.context2D.restore();
      }

      switch (type) {
        case ImageEditorImageType.IMG_DATA:
          imgData = this.context2D?.getImageData(0, 0, sW, sH);
          break;
        default: /* base64url */
        case ImageEditorImageType.BASE64URL:
          imgData = canvas.toDataURL('image/jpeg');
          break;
      }

      return {
        data: imgData,
        type: type,
      };
    } else {
      throw new Error(this.props.errorMessages.canvas);
    }
  }

  switchCamera() {
    if (this.state.numCameras < 1) {
      throw new Error(this.props.errorMessages.noCameraAccessible);
    } else if (this.state.numCameras < 2) {
      console.error('Error: Unable to switch camera. Only one device is accessible.'); // console only
    }
    const newFacingMode = this.state.facingMode === 'user' ? 'environment' : 'user';
    this.setState({facingMode: newFacingMode});
    return newFacingMode;
  }

  getNumCameras() {
    return this.state.numCameras;
  }

  toggleTorchOn(): boolean {
    const torchOn = !this.state.torchOn;
    this.setState({torchOn});
    return torchOn;
  }

  render() {
    const mirrored = this.state.facingMode === 'user';
    let aspectRatio = this.props.aspectRatio;
    return <div ref={this.cameraRefs.container}
                style={{
                  width: "100%",
                  height: "100%",
                  ...(aspectRatio === "cover" ? {
                      position: "absolute",
                      bottom: 0,
                      top: 0,
                      left: 0,
                      right: 0,
                    }
                    : {
                      position: "relative",
                      paddingBottom: `${100 / aspectRatio}%`,
                    })
                }}>
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
      }}>
        {this.state.notSupported ? <Typography>{this.props.errorMessages.noCameraAccessible}</Typography> : null}
        {this.state.permissionDenied ? <Typography>{this.props.errorMessages.permissionDenied}</Typography> : null}
        <video
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 0,
            transform: `rotateY(${(mirrored ? '180deg' : '0deg')})`,
          }}
          ref={this.cameraRefs.player}
          id="video"
          muted={true}
          autoPlay={true}
          playsInline={true}
          onLoadedData={() => {
            this.props.videoReadyCallback?.(this.cameraRefs);
          }}
        />
        <canvas style={{display: "none"}} ref={this.cameraRefs.canvas}/>
        <canvas style={{position: "absolute", left: 0, top: 0, transform: `rotateY(${(mirrored ? '180deg' : '0deg')})`}}
                ref={this.cameraRefs.overlay}/>
      </div>
    </div>
  }
}

async function shouldSwitchToCamera(facingMode: FacingMode): Promise<string | undefined> {
  const cameras: string[] = [];
  if (facingMode === 'environment') {
    await navigator.mediaDevices.enumerateDevices().then((devices) => {
      const videoDevices = devices.filter((i) => i.kind === 'videoinput');
      videoDevices.forEach((device) => {
        //@ts-ignore
        const capabilities = (device as InputDeviceInfo).getCapabilities();
        if (capabilities.facingMode && capabilities.facingMode.indexOf('environment') >= 0 && capabilities.deviceId) {
          cameras.push(capabilities.deviceId);
        }
      });
    });
  }

  if (cameras.length > 1) {
    return cameras.pop();
  }

  return undefined;
};

async function initCameraStream(
  stream: Stream,
  setStream: SetStream,
  facingMode: FacingMode,
  videoSourceDeviceId: string | undefined,
  setNumCameras: SetNumCameras,
  setNotSupported: SetNotSupported,
  setPermissionDenied: SetPermissionDenied,
  isMounted: boolean,
) {
  // stop any active streams in the window
  if (stream) {
    stream.getTracks().forEach((track) => {
      track.stop();
    });
  }

  let cameraDeviceId;

  const switchToCamera = await shouldSwitchToCamera(facingMode);
  if (switchToCamera) {
    cameraDeviceId = switchToCamera;
  } else {
    cameraDeviceId = videoSourceDeviceId ? {exact: videoSourceDeviceId} : undefined;
  }

  const constraints = {
    audio: false,
    video: {
      deviceId: cameraDeviceId,
      facingMode: facingMode,
    },
  };

  if (navigator.mediaDevices?.getUserMedia) {
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream) => {
        if (isMounted) {
          setStream(handleSuccess(stream, setNumCameras));
        }
      })
      .catch((err) => {
        handleError(err, setNotSupported, setPermissionDenied);
      });
  } else {
    setNotSupported(true);
  }
}

const handleSuccess = (stream: MediaStream, setNumCameras: SetNumCameras) => {
  navigator.mediaDevices
    .enumerateDevices()
    .then((r) => setNumCameras(r.filter((i) => i.kind === 'videoinput').length));

  return stream;
};

const handleError = (error: Error, setNotSupported: SetNotSupported, setPermissionDenied: SetPermissionDenied) => {
  console.error(error);

  //https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
  if (error.name === 'PermissionDeniedError') {
    setPermissionDenied(true);
  } else {
    setNotSupported(true);
  }
};

enum CameraMode {
  CAPTURE,
  PREVIEW,
  EDIT,
}

function ControlsGrid({controls}: { controls: ReactElement[] }): ReactElement {
  return <Grid container>
    {controls.map((control, index) => <Grid xs={12 / controls.length} key={index}>
      <StyledBoxRow style={{width: "100%", height: "100%", alignItems: "center", justifyContent: "center"}}>
        {control}
      </StyledBoxRow>
    </Grid>)}
  </Grid>;
}

type CropSize = {
  width: number,
  height: number,
}

async function uploadImage(image: ImageEditorImage, storedDataSource: string, storedDataPath: string | null, setUploadedFiles: (urls: string[]) => void, deferUpload?: boolean): Promise<void> {
  let blob: Blob;
  try {
    switch (image.type) {
      case ImageEditorImageType.IMG_DATA:
        // TODO support this.
        break;
      default:
      case ImageEditorImageType.BASE64URL:
        blob = FileUtil.dataUrlToBlob(image.data);
    }
  } catch (e) {
    // Continue.
  }
  if (!blob) {
    throw new Error("Error parsing image data");
  }
  if (deferUpload) {
    setUploadedFiles([URL.createObjectURL(blob)]);
    return;
  }
  return FileUtil.uploadBlob(blob, storedDataSource, storedDataPath).then(url => setUploadedFiles([url]));
}

type CameraResult = {
  image: ImageEditorImage,
  croppedImage: ImageEditorImage,
}

export function StyledCamera(props: {
  facingMode?: FacingMode,
  initialImage?: ImageEditorImage,
  imageOptions?: ImageOptions,
  imageEditor?: ImageEditor,
  onDone?: (image: ImageEditorImage) => void,
  autoUpload?: boolean,
  onUploadDone?: (urls: string[]) => void,
  videoReadyCallback?: (refs: CameraRefs) => void,
  videoDestroyCallback?: () => void,
}): ReactElement {
  const cameraRef = useRef<StyledCameraContent | null>(null);
  const [mode, setMode] = useState<CameraMode>(props.initialImage ? CameraMode.PREVIEW : CameraMode.CAPTURE);
  const [result, setResult] = useState<CameraResult>(props.initialImage ? {
    image: props.initialImage,
    croppedImage: props.initialImage
  } : null);
  const [numCameras, setNumCameras] = useState<number>(0);
  const options = props.imageOptions;
  let cropSize: CropSize;
  const keepAspectRatio = options?.keepAspectRatio;
  if (keepAspectRatio) {
    const windowAspectRatio = window.innerWidth / window.innerHeight;
    if (windowAspectRatio < keepAspectRatio) {
      cropSize = {
        width: window.innerWidth,
        height: window.innerWidth / keepAspectRatio,
      };
    } else {
      cropSize = {
        width: window.innerHeight * keepAspectRatio,
        height: window.innerHeight,
      };
    }
  }
  return <div style={{width: "100%", height: "100%", position: "relative"}}>
    {mode === CameraMode.CAPTURE
      ? <>
        <StyledCameraContent
          videoReadyCallback={props.videoReadyCallback}
          videoDestroyCallback={props.videoDestroyCallback}
          facingMode={props.facingMode}
          ref={cameraRef}
          numCamerasCallback={numCameras => setNumCameras(numCameras)}/>
        <StyledBoxRow style={{
          position: "absolute",
          width: "100%",
          height: SZ_XLG,
          background: translucentBlack,
          bottom: 0,
          alignItems: "center",
          justifyContent: "center"
        }}>
          <ControlsGrid controls={[
            null,
            <IconButton
              onClick={() => {
                const image = cameraRef.current?.takePhoto() as ImageEditorImage;
                const croppedImage = cameraRef.current?.cropImage(keepAspectRatio);
                setResult({image, croppedImage});
                setMode(CameraMode.PREVIEW);
              }}
              style={{background: colorBlue, color: "white", padding: PD_XLG}}>
              <CameraAltOutlined style={{width: SZ_SM, height: SZ_SM}}/>
            </IconButton>,
            numCameras > 1
              ? <IconButton
                onClick={() => cameraRef.current?.switchCamera()}
                style={{background: nightGray, color: "white", padding: PD_XLG}}>
                <CameraswitchOutlined style={{width: SZ_SMSM, height: SZ_SMSM}}/>
              </IconButton>
              : null,
          ]}/>
        </StyledBoxRow>
      </>
      : null}
    {mode === CameraMode.PREVIEW
      ? <>
        <img src={result.image.data} style={{
          width: "100%",
          height: "100%",
          position: "absolute",
          objectFit: (result.image.cropped ? "contain" : "cover")
        }}/>
        {cropSize
          ? <div style={{
            width: cropSize.width,
            height: cropSize.height,
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            border: "1px solid rgba(255, 255, 255, 0.5)",
            boxSizing: "border-box",
            boxShadow: "0 0 0 9999em",
            color: "rgba(0, 0, 0, 0.5)",
            overflow: "hidden",
          }}/>
          : null}
        {Boolean(props.imageEditor)
          ? <Button
            onClick={() => setMode(CameraMode.EDIT)}
            startIcon={<EditOutlined/>} variant="contained"
            style={{borderRadius: SZ_SM, position: "absolute", top: 16, left: 16}}>
            Edit
          </Button>
          : null}
        <StyledBoxRow style={{
          position: "absolute",
          width: "100%",
          height: SZ_XLG,
          background: translucentBlack,
          bottom: 0,
          alignItems: "center",
          justifyContent: "center"
        }}>
          <ControlsGrid controls={[
            <IconButton
              onClick={() => {
                setResult(null);
                setMode(CameraMode.CAPTURE);
              }}
              style={{background: nightGray, color: "white", padding: PD_XLG}}>
              <ReplayOutlined style={{width: SZ_SMSM, height: SZ_SMSM}}/>
            </IconButton>,
            null,
            <IconButton
              onClick={() => {
                props.onDone?.(result.croppedImage);
                if (props.autoUpload) {
                  BaseApp.CONTEXT.showProgressDialog("Please wait...", null, async function () {
                    await uploadImage(
                      result.croppedImage,
                      "camera-photos",
                      null,
                      (urls: string[]) => props.onUploadDone?.(urls),
                      true /* defer upload */);
                  });
                }
              }}
              style={{background: colorGreen, color: "white", padding: PD_XLG}}>
              <CheckOutlined style={{width: SZ_SM, height: SZ_SM}}/>
            </IconButton>,
          ]}/>
        </StyledBoxRow>
      </>
      : null}
    {mode === CameraMode.EDIT
      ? <>
        {props.imageEditor?.render(result.image, (image: ImageEditorImage) => {
          setResult({image: image, croppedImage: image});
          setMode(CameraMode.PREVIEW);
        }, options)}
      </>
      : null}
  </div>;
}